import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, aiKeysApi } from '../utils/api';
import {
    User,
    Key,
    CreditCard,
    Shield,
    Bell,
    Save,
    Eye,
    EyeOff,
    CheckCircle,
    Loader2,
    LogOut,
    Trash2,
    ExternalLink,
    Zap,
    Info
} from 'lucide-react';

type Tab = 'profile' | 'apikeys' | 'billing' | 'security' | 'notifications';

export default function Settings() {
    const [activeTab, setActiveTab] = useState<Tab>('profile');
    const { user, logout } = useAuthStore();

    const tabs = [
        { id: 'profile' as Tab, label: 'Profile', icon: User },
        { id: 'apikeys' as Tab, label: 'API Keys', icon: Key },
        { id: 'billing' as Tab, label: 'Billing', icon: CreditCard },
        { id: 'security' as Tab, label: 'Security', icon: Shield },
        { id: 'notifications' as Tab, label: 'Notifications', icon: Bell },
    ];

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold">Settings</h1>
                <p className="text-dark-400 mt-1">Manage your account and preferences</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
                {/* Sidebar */}
                <div className="lg:w-64 space-y-1">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeTab === tab.id
                                ? 'bg-primary-600/20 text-primary-400'
                                : 'hover:bg-dark-800 text-dark-300'
                                }`}
                        >
                            <tab.icon className="w-5 h-5" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="flex-1">
                    {activeTab === 'profile' && <ProfileSettings user={user} />}
                    {activeTab === 'apikeys' && <APIKeysSettings />}
                    {activeTab === 'billing' && <BillingSettings user={user} />}
                    {activeTab === 'security' && <SecuritySettings logout={logout} />}
                    {activeTab === 'notifications' && <NotificationSettings />}
                </div>
            </div>
        </div>
    );
}

// Profile Settings
function ProfileSettings({ user }: { user: any }) {
    const [name, setName] = useState(user?.name || '');
    const [email, setEmail] = useState(user?.email || '');
    const [saved, setSaved] = useState(false);

    const updateMutation = useMutation({
        mutationFn: (data: any) => api.put('/auth/profile', data),
        onSuccess: () => {
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        }
    });

    const handleSave = () => {
        updateMutation.mutate({ name, email });
    };

    return (
        <div className="space-y-6">
            <div className="card p-6">
                <h2 className="text-xl font-semibold mb-6">Profile Information</h2>

                <div className="space-y-4">
                    <div>
                        <label className="label">Full Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="input"
                        />
                    </div>

                    <div>
                        <label className="label">Email Address</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="input"
                        />
                    </div>

                    <div className="flex items-center gap-4 pt-4">
                        <button
                            onClick={handleSave}
                            disabled={updateMutation.isPending}
                            className="btn btn-primary flex items-center gap-2"
                        >
                            {updateMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            Save Changes
                        </button>
                        {saved && (
                            <span className="text-green-400 flex items-center gap-2">
                                <CheckCircle className="w-4 h-4" />
                                Saved!
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Avatar */}
            <div className="card p-6">
                <h2 className="text-xl font-semibold mb-6">Avatar</h2>
                <div className="flex items-center gap-6">
                    <div className="w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center text-3xl font-bold">
                        {name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div>
                        <button className="btn btn-secondary">Change Avatar</button>
                        <p className="text-dark-500 text-sm mt-2">JPG, PNG. Max 2MB</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

// API Keys Settings
function APIKeysSettings() {
    const queryClient = useQueryClient();
    const [showOpenAI, setShowOpenAI] = useState(false);
    const [showAnthropic, setShowAnthropic] = useState(false);
    const [showGoogle, setShowGoogle] = useState(false);
    const [showXai, setShowXai] = useState(false);
    const [openaiKey, setOpenaiKey] = useState('');
    const [anthropicKey, setAnthropicKey] = useState('');
    const [googleKey, setGoogleKey] = useState('');
    const [xaiKey, setXaiKey] = useState('');
    const [usePlatformKey, setUsePlatformKey] = useState(true);
    const [saved, setSaved] = useState(false);

    // Fetch existing keys from backend
    const { data: existingKeys, isLoading } = useQuery({
        queryKey: ['ai-keys'],
        queryFn: async () => {
            const res = await aiKeysApi.get();
            return res.data;
        }
    });

    // Update platform key preference
    useEffect(() => {
        if (existingKeys?.usePlatformKey !== undefined) {
            setUsePlatformKey(existingKeys.usePlatformKey);
        }
    }, [existingKeys]);

    // Update keys mutation
    const updateKeysMutation = useMutation({
        mutationFn: (keys: { openai?: string; anthropic?: string; google?: string; xai?: string; usePlatformKey?: boolean }) =>
            aiKeysApi.update(keys),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ai-keys'] });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        }
    });

    // Remove key mutation
    const removeKeyMutation = useMutation({
        mutationFn: (provider: 'openai' | 'anthropic' | 'google' | 'xai') =>
            aiKeysApi.remove(provider),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ai-keys'] });
        }
    });

    const handleSave = () => {
        const keys: { openai?: string; anthropic?: string; google?: string; xai?: string; usePlatformKey?: boolean } = {
            usePlatformKey
        };
        if (openaiKey && !openaiKey.includes('•')) keys.openai = openaiKey;
        if (anthropicKey && !anthropicKey.includes('•')) keys.anthropic = anthropicKey;
        if (googleKey && !googleKey.includes('•')) keys.google = googleKey;
        if (xaiKey && !xaiKey.includes('•')) keys.xai = xaiKey;

        updateKeysMutation.mutate(keys);
    };

    const handleRemove = (provider: 'openai' | 'anthropic' | 'google' | 'xai') => {
        if (confirm(`Are you sure you want to remove the ${provider} API key?`)) {
            removeKeyMutation.mutate(provider);
            if (provider === 'openai') setOpenaiKey('');
            if (provider === 'anthropic') setAnthropicKey('');
            if (provider === 'google') setGoogleKey('');
            if (provider === 'xai') setXaiKey('');
        }
    };

    const apiServices = [
        {
            id: 'openai' as const,
            name: 'OpenAI',
            description: 'GPT-4, DALL-E, Whisper',
            key: openaiKey || existingKeys?.openai || '',
            setKey: setOpenaiKey,
            show: showOpenAI,
            setShow: setShowOpenAI,
            placeholder: 'sk-...',
            link: 'https://platform.openai.com/api-keys',
            icon: '🤖',
            isConnected: existingKeys?.openai
        },
        {
            id: 'anthropic' as const,
            name: 'Anthropic',
            description: 'Claude AI models',
            key: anthropicKey || existingKeys?.anthropic || '',
            setKey: setAnthropicKey,
            show: showAnthropic,
            setShow: setShowAnthropic,
            placeholder: 'sk-ant-...',
            link: 'https://console.anthropic.com/settings/keys',
            icon: '🧠',
            isConnected: existingKeys?.anthropic
        },
        {
            id: 'google' as const,
            name: 'Google AI',
            description: 'Gemini models',
            key: googleKey || existingKeys?.google || '',
            setKey: setGoogleKey,
            show: showGoogle,
            setShow: setShowGoogle,
            placeholder: 'AIza...',
            link: 'https://aistudio.google.com/apikey',
            icon: '🌐',
            isConnected: existingKeys?.google
        },
        {
            id: 'xai' as const,
            name: 'xAI',
            description: 'Grok models',
            key: xaiKey || existingKeys?.xai || '',
            setKey: setXaiKey,
            show: showXai,
            setShow: setShowXai,
            placeholder: 'xai-...',
            link: 'https://console.x.ai/team/default/api-keys',
            icon: '𝕏',
            isConnected: existingKeys?.xai
        }
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Platform Key Toggle */}
            <div className="card p-6 border-2 border-primary-500/30 bg-gradient-to-r from-primary-900/20 to-transparent">
                <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                        <div className="p-3 bg-primary-500/20 rounded-xl">
                            <Zap className="w-6 h-6 text-primary-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold flex items-center gap-2">
                                Sundarban AI
                                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">INCLUDED</span>
                            </h2>
                            <p className="text-dark-400 text-sm mt-1">
                                Powered by Sundarban AI - our built-in AI engine. No API keys required!
                            </p>
                            <div className="flex items-center gap-4 mt-3 text-sm">
                                <div className="flex items-center gap-1 text-dark-300">
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    50+ Open Source Models
                                </div>
                                <div className="flex items-center gap-1 text-dark-300">
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    Daily Free Allowance
                                </div>
                                <div className="flex items-center gap-1 text-dark-300">
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    Llama, Mistral, Flux & More
                                </div>
                            </div>
                        </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={usePlatformKey}
                            onChange={(e) => setUsePlatformKey(e.target.checked)}
                            className="sr-only peer"
                        />
                        <div className="w-14 h-7 bg-dark-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary-500"></div>
                    </label>
                </div>

                {usePlatformKey && (
                    <div className="mt-4 p-3 bg-dark-800/50 rounded-lg">
                        <div className="flex items-center gap-2 text-sm">
                            <Info className="w-4 h-4 text-primary-400" />
                            <span className="text-dark-300">
                                <strong className="text-white">Daily Limit:</strong> Based on your plan.
                                Free: 50 neurons • Starter: 200 neurons • Pro: 1,000 neurons
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* External API Keys */}
            <div className="card p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-xl font-semibold">External AI Provider Keys</h2>
                        <p className="text-dark-400 text-sm">
                            {usePlatformKey
                                ? 'Optional: Add your own keys for premium models (GPT-4, Claude, etc.)'
                                : 'Required: Add at least one API key to use AI features'
                            }
                        </p>
                    </div>
                    {saved && (
                        <span className="text-green-400 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Saved!
                        </span>
                    )}
                </div>

                <div className="space-y-6">
                    {apiServices.map((service) => (
                        <div key={service.name} className="p-4 bg-dark-800/50 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl">{service.icon}</span>
                                    <div>
                                        <h3 className="font-semibold">{service.name}</h3>
                                        <p className="text-dark-400 text-sm">{service.description}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    {service.isConnected && (
                                        <button
                                            onClick={() => handleRemove(service.id)}
                                            className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1"
                                        >
                                            <Trash2 className="w-3 h-3" /> Remove
                                        </button>
                                    )}
                                    <a
                                        href={service.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary-400 hover:text-primary-300 text-sm flex items-center gap-1"
                                    >
                                        Get Key <ExternalLink className="w-3 h-3" />
                                    </a>
                                </div>
                            </div>
                            <div className="relative">
                                <input
                                    type={service.show ? 'text' : 'password'}
                                    value={service.key}
                                    onChange={(e) => service.setKey(e.target.value)}
                                    className="input pr-10"
                                    placeholder={service.isConnected ? 'Key saved (enter new to replace)' : service.placeholder}
                                />
                                <button
                                    onClick={() => service.setShow(!service.show)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-white"
                                >
                                    {service.show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            {
                                service.isConnected && (
                                    <div className="flex items-center gap-2 mt-2 text-green-400 text-sm">
                                        <CheckCircle className="w-4 h-4" />
                                        Connected
                                    </div>
                                )
                            }
                        </div>
                    ))}
                </div>

                <button
                    onClick={handleSave}
                    disabled={updateKeysMutation.isPending}
                    className="btn btn-primary mt-6 flex items-center gap-2"
                >
                    {updateKeysMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Save className="w-4 h-4" />
                    )}
                    Save API Keys
                </button>
            </div>

            {/* MCP API Key */}
            <div className="card p-6">
                <h2 className="text-xl font-semibold mb-2">MCP API Key</h2>
                <p className="text-dark-400 text-sm mb-4">Use this key to connect AI agents to your aiOps workspace</p>

                <div className="p-4 bg-dark-800/50 rounded-lg">
                    <div className="flex items-center justify-between">
                        <code className="text-sm text-primary-400">aiops_••••••••••••••••</code>
                        <button className="btn btn-secondary text-sm">Generate New Key</button>
                    </div>
                </div>
            </div>
        </div >
    );
}

// Billing Settings
function BillingSettings({ user }: { user: any }) {
    const plan = user?.subscription?.plan || 'free';

    const plans = [
        {
            id: 'free',
            name: 'Free Trial',
            price: 0,
            period: '7 days',
            credits: '50/day',
            features: ['All 6 modules', 'Sundarban AI', '1 project', '7-day trial'],
            highlight: false,
            cta: 'Current'
        },
        {
            id: 'launchpad',
            name: '🚀 Launch Pad',
            price: 9,
            period: '/month',
            credits: '2,000/day',
            features: ['All 6 modules', 'Sundarban AI', 'BYOK support', '5 projects', '2,000 credits/day', 'Cancel anytime'],
            highlight: true,
            cta: 'Start for $9'
        },
        {
            id: 'scale',
            name: 'Scale',
            price: 49,
            period: '/month',
            credits: '10,000/day',
            features: ['Everything in Launch Pad', 'Unlimited projects', '5 team members', 'API access', 'Priority support'],
            highlight: false,
            cta: 'Upgrade'
        },
        {
            id: 'enterprise',
            name: 'Enterprise',
            price: -1,
            period: 'custom',
            credits: 'Unlimited',
            features: ['Everything in Scale', 'Unlimited team', 'Custom integrations', 'SLA', 'Dedicated support'],
            highlight: false,
            cta: 'Contact Us'
        }
    ];

    const currentPlan = plans.find(p => p.id === plan) || plans[0];

    return (
        <div className="space-y-6">
            {/* Current Plan */}
            <div className="card p-6">
                <h2 className="text-xl font-semibold mb-4">Current Plan</h2>
                <div className={`flex items-center justify-between p-4 rounded-lg ${plan === 'launchpad'
                    ? 'bg-gradient-to-r from-primary-600/30 to-purple-600/30 border border-primary-500/50'
                    : 'bg-primary-600/20 border border-primary-500/30'
                    }`}>
                    <div>
                        <h3 className="font-semibold text-lg">{currentPlan.name}</h3>
                        <p className="text-dark-400 text-sm">
                            {plan === 'free'
                                ? 'Upgrade to Launch Pad for full access!'
                                : `${currentPlan.credits} credits • Your subscription is active`
                            }
                        </p>
                    </div>
                    {plan === 'free' && (
                        <button className="btn btn-primary">Upgrade to Launch Pad - $9/mo</button>
                    )}
                </div>
            </div>

            {/* Plans Grid */}
            <div className="card p-6">
                <h2 className="text-xl font-semibold mb-2">Choose Your Plan</h2>
                <p className="text-dark-400 text-sm mb-6">Full access to all features. Cancel anytime.</p>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {plans.map((p) => (
                        <div
                            key={p.id}
                            className={`p-6 rounded-xl border relative ${p.highlight
                                ? 'border-primary-500 bg-gradient-to-b from-primary-500/20 to-transparent'
                                : plan === p.id
                                    ? 'border-green-500 bg-green-500/10'
                                    : 'border-dark-700 hover:border-dark-600'
                                }`}
                        >
                            {p.highlight && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                                    MOST POPULAR
                                </div>
                            )}
                            {plan === p.id && (
                                <div className="absolute -top-3 right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                                    CURRENT
                                </div>
                            )}

                            <h3 className="font-semibold text-lg">{p.name}</h3>
                            <div className="my-4">
                                {p.price === -1 ? (
                                    <span className="text-2xl font-bold">Custom</span>
                                ) : (
                                    <>
                                        <span className="text-3xl font-bold">${p.price}</span>
                                        <span className="text-dark-400">{p.period}</span>
                                    </>
                                )}
                            </div>

                            <div className="mb-4 p-2 bg-dark-800/50 rounded-lg text-center">
                                <span className="text-primary-400 font-semibold">{p.credits}</span>
                                <span className="text-dark-400 text-sm"> credits</span>
                            </div>

                            <ul className="space-y-2 mb-6">
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-center gap-2 text-sm text-dark-300">
                                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                                        {f}
                                    </li>
                                ))}
                            </ul>

                            <button
                                className={`w-full ${plan === p.id
                                    ? 'btn btn-secondary cursor-default'
                                    : p.highlight
                                        ? 'btn btn-primary'
                                        : 'btn btn-secondary hover:bg-dark-600'
                                    }`}
                                disabled={plan === p.id}
                            >
                                {plan === p.id ? '✓ Current Plan' : p.cta}
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Daily Usage */}
            <div className="card p-6">
                <h2 className="text-xl font-semibold mb-4">Today's Usage</h2>
                <div className="space-y-4">
                    <div>
                        <div className="flex justify-between text-sm mb-1">
                            <span>AI Credits</span>
                            <span className="text-primary-400">
                                {plan === 'free' ? '12 / 50' : plan === 'launchpad' ? '450 / 2,000' : '1,200 / 10,000'}
                            </span>
                        </div>
                        <div className="h-3 bg-dark-700 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-primary-500 to-purple-500 rounded-full transition-all"
                                style={{ width: plan === 'free' ? '24%' : plan === 'launchpad' ? '22.5%' : '12%' }}
                            />
                        </div>
                        <p className="text-dark-500 text-xs mt-1">Resets daily at midnight UTC</p>
                    </div>
                    <div>
                        <div className="flex justify-between text-sm mb-1">
                            <span>Projects</span>
                            <span>2 / {plan === 'free' ? '1' : plan === 'launchpad' ? '5' : '∞'}</span>
                        </div>
                        <div className="h-3 bg-dark-700 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-blue-500 rounded-full"
                                style={{ width: plan === 'free' ? '100%' : plan === 'launchpad' ? '40%' : '10%' }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* BYOK Notice */}
            {(plan === 'launchpad' || plan === 'scale' || plan === 'enterprise') && (
                <div className="card p-6 border-dashed border-2 border-dark-600">
                    <div className="flex items-start gap-4">
                        <div className="p-2 bg-green-500/20 rounded-lg">
                            <Key className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                            <h3 className="font-semibold">Unlimited AI with Your Own Keys</h3>
                            <p className="text-dark-400 text-sm mt-1">
                                Add your OpenAI, Anthropic, Google, or xAI keys in the API Keys tab for unlimited AI generation.
                                Your keys, your cost, no limits!
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Security Settings
function SecuritySettings({ logout }: { logout: () => void }) {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const changePasswordMutation = useMutation({
        mutationFn: (data: any) => api.put('/auth/password', data)
    });

    const handleChangePassword = () => {
        if (newPassword !== confirmPassword) {
            alert('Passwords do not match');
            return;
        }
        changePasswordMutation.mutate({ currentPassword, newPassword });
    };

    return (
        <div className="space-y-6">
            {/* Change Password */}
            <div className="card p-6">
                <h2 className="text-xl font-semibold mb-6">Change Password</h2>
                <div className="space-y-4 max-w-md">
                    <div>
                        <label className="label">Current Password</label>
                        <input
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className="input"
                        />
                    </div>
                    <div>
                        <label className="label">New Password</label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="input"
                        />
                    </div>
                    <div>
                        <label className="label">Confirm New Password</label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="input"
                        />
                    </div>
                    <button
                        onClick={handleChangePassword}
                        disabled={changePasswordMutation.isPending}
                        className="btn btn-primary"
                    >
                        Update Password
                    </button>
                </div>
            </div>

            {/* Sessions */}
            <div className="card p-6">
                <h2 className="text-xl font-semibold mb-4">Active Sessions</h2>
                <div className="p-4 bg-dark-800/50 rounded-lg flex items-center justify-between">
                    <div>
                        <h3 className="font-medium">Current Session</h3>
                        <p className="text-dark-400 text-sm">Chrome on Windows • Active now</p>
                    </div>
                    <span className="text-green-400 text-sm">Current</span>
                </div>
            </div>

            {/* Danger Zone */}
            <div className="card p-6 border-red-500/30">
                <h2 className="text-xl font-semibold mb-4 text-red-400">Danger Zone</h2>
                <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-dark-800/50 rounded-lg">
                        <div>
                            <h3 className="font-medium">Sign out everywhere</h3>
                            <p className="text-dark-400 text-sm">Sign out of all sessions including this one</p>
                        </div>
                        <button onClick={logout} className="btn bg-red-500/20 text-red-400 hover:bg-red-500/30 flex items-center gap-2">
                            <LogOut className="w-4 h-4" />
                            Sign Out
                        </button>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-dark-800/50 rounded-lg">
                        <div>
                            <h3 className="font-medium">Delete Account</h3>
                            <p className="text-dark-400 text-sm">Permanently delete your account and all data</p>
                        </div>
                        <button className="btn bg-red-500/20 text-red-400 hover:bg-red-500/30 flex items-center gap-2">
                            <Trash2 className="w-4 h-4" />
                            Delete
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Notification Settings
function NotificationSettings() {
    const [emailNotifications, setEmailNotifications] = useState({
        marketing: true,
        updates: true,
        reports: true,
        alerts: true
    });

    return (
        <div className="space-y-6">
            <div className="card p-6">
                <h2 className="text-xl font-semibold mb-6">Email Notifications</h2>
                <div className="space-y-4">
                    {[
                        { key: 'marketing', label: 'Marketing & Promotions', desc: 'Receive updates about new features and offers' },
                        { key: 'updates', label: 'Product Updates', desc: 'Important updates about aiOps' },
                        { key: 'reports', label: 'Weekly Reports', desc: 'Get weekly analytics summaries' },
                        { key: 'alerts', label: 'Alerts & Warnings', desc: 'Important alerts about your projects' }
                    ].map((item) => (
                        <div key={item.key} className="flex items-center justify-between p-4 bg-dark-800/50 rounded-lg">
                            <div>
                                <h3 className="font-medium">{item.label}</h3>
                                <p className="text-dark-400 text-sm">{item.desc}</p>
                            </div>
                            <button
                                onClick={() => setEmailNotifications({
                                    ...emailNotifications,
                                    [item.key]: !emailNotifications[item.key as keyof typeof emailNotifications]
                                })}
                                className={`w-12 h-6 rounded-full transition-colors relative ${emailNotifications[item.key as keyof typeof emailNotifications]
                                    ? 'bg-primary-500'
                                    : 'bg-dark-600'
                                    }`}
                            >
                                <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${emailNotifications[item.key as keyof typeof emailNotifications]
                                    ? 'translate-x-6'
                                    : 'translate-x-0.5'
                                    }`} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
